import { Delete, Edit, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  TablePagination,
  CircularProgress,
  Select,
  MenuItem,
  Tabs,
  Tab,
  OutlinedInput,
  InputAdornment,
  Grid,
  Tooltip,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { SearchIcon } from "lucide-react";
import BillReport from "../reports/billReport";
import ThermalBillReport from "../reports/billReport/thermal";
import { useSelector, useDispatch } from "react-redux";
import CollectionCenterDropdown from "../../../components/CollectionCenterDropdown";
import { fetchProfileData } from "../../../redux/reducers/profileSlice";
interface EditedData {
  assetName: string;
  modelNumber: string;
  purchaseDate: string;
  price: number;
  quantity: number;
  supplier: string;
  warrantyPeriod: string;
  warrantyIn: string;
  expiryDate: string;
  categoryId: string;
  locationId: string;
  isNeededForTest: boolean;
}

function Samples() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const [totalAssets, setTotalAssets] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>("");
  const [searchType, setSearchType] = useState("All");
  const [samples, setSamples] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState("Today");
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("MRN");
  const [billData, setBillData] = useState<any>(null);
  const [openPrintDialogPayment, setOpenPrintDialogPayment] = useState(false);
  const [printFormat, setPrintFormat] = useState<"a4" | "thermal">("a4");
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] =
    useState<string>("");
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const accessConfigurations = profile?.data?.accessConfigurations || {};
  const labAccess = accessConfigurations?.Lab || {};

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    const today = new Date();
    const formatDate = (date: Date) => date.toISOString().split("T")[0];

    switch (value) {
      case "Today":
        const todayStr = formatDate(today);
        setFromDate(todayStr);
        setToDate(todayStr);
        break;

      case "Yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const yesterdayStr = formatDate(yesterday);
        setFromDate(yesterdayStr);
        setToDate(yesterdayStr);
        break;

      case "Last 7 days":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        setFromDate(formatDate(sevenDaysAgo));
        setToDate(formatDate(today));
        break;

      case "Last 1 month":
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        setFromDate(formatDate(oneMonthAgo));
        setToDate(formatDate(today));
        break;

      case "Last 6 months":
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        setFromDate(formatDate(sixMonthsAgo));
        setToDate(formatDate(today));
        break;

      case "Last 1 year":
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        setFromDate(formatDate(oneYearAgo));
        setToDate(formatDate(today));
        break;

      case "custom":
        // Don't change dates for custom - let user select manually
        break;

      default:
        break;
    }
  };
  const handleSearchTypeChange = (event: any) => {
    setSearch(event.target.value);
    setSearchValue("");
  };
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    getSamples(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = Number.parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    getSamples(page, newRowsPerPage);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const tabToSearchType = [
      "All",
      "Pending Dues",
      "Pending Sample Collection",
    ];
    setSearchType(tabToSearchType[newValue]);
  };

  useEffect(() => {
    // Fetch user profile on component mount
    dispatch(fetchProfileData() as any);
  }, [dispatch]);

  useEffect(() => {
    if (selectedCollectionCenterId) {
      getSamples(page, rowsPerPage);
    }
  }, [searchType, fromDate, toDate, selectedCollectionCenterId]);

  const handleCancelDelete = () => {
    setOpenDeleteModal(false);
    setDeleteItemId(null);
  };

const calculateAge = (dob: string, referenceDate?: string): string => {
  const birthDate = new Date(dob);
  const today = referenceDate ? new Date(referenceDate) : new Date();
  
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();
  
  // Adjust for negative days
  if (days < 0) {
    months--;
    const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += lastMonth.getDate();
  }
  
  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }
  
  // Ensure non-negative values
  years = Math.max(0, years);
  months = Math.max(0, months);
  days = Math.max(0, days);
  
  // Return in format: 0y-0m-0d
  return `${years}y-${months}m-${days}d`;
};

  const handleConfirmDelete = async () => {
    if (deleteItemId) {
      await deleteSample(deleteItemId);
      setOpenDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const handleEditClick = (item: any) => {
    console.log("edit item", item);

    navigate(`/sample-collection`, {
      state: {
        patient: item?.patientData,
        TestData: item,
        paymentDetail: item?.paymentDetail,
        edit: true,
      },
    });
  };

  const getSamples = async (
    currentPage = page,
    currentRowsPerPage = rowsPerPage
  ) => {
    try {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAB_URL}/sample-collection/findAll`,
          {
            params: {
              count: currentRowsPerPage,
              pageNo: currentPage + 1,
              fromDate,
              toDate,
              sampleStatus: searchType,
              searchBy: search,
              search: searchValue,
              collectionCenterId: selectedCollectionCenterId,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          setSamples(response?.data?.data);
          setTotalAssets(response?.data?.totalCount || 0);
        }
      } catch (apiError) {
        console.error("API Error:", apiError);
        toast.error(
          apiError instanceof Error
            ? apiError.message
            : "An unexpected error occurred"
        );
        setSamples([]);
        setTotalAssets(0);
      }
    } catch (error) {
      console.error("Error fetching samples:", error);
    } finally {
      setLoading(false);
    }
  };
  const getBillData = async ({
    id,
    format = "a4",
  }: {
    id: any;
    format?: "a4" | "thermal";
  }) => {
    if (!id) return;
    setPrintFormat(format);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/patient-result/getPaymentDetails/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        setBillData(response?.data?.data);
        setOpenPrintDialogPayment(true);
      }
    } catch (error: any) {
      console.error("Error fetching patient types:", error);
      toast.error(error?.response.data.message || "Error fetching patient types");
    }
  };
  const deleteSample = async (id: string) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/sample-collection/deleteOne/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        toast.success(response?.data?.message);
        getSamples();
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error: any) {
      console.error("Error deleting sample:", error);
      toast.error(error?.response.data.message || "Error deleting sample");
    }
  };

  const handleDeleteClick = (id: any) => {
    setDeleteItemId(id);
    setOpenDeleteModal(true);
  };
  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      getSamples(page, rowsPerPage);
    }
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#ffffff",
          p: 2,
          mb: 2,
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #e9ecef",
        }}
      >
        <Typography
          sx={{
            color: "#495057",
            fontSize: "20px",
            fontWeight: "600",
            textTransform: "none",
            letterSpacing: "normal",
          }}
        >
          Samples
        </Typography>
        <Box sx={{ minWidth: "160px", width: { xs: "100%", sm: "160px" } }}>
          <CollectionCenterDropdown
            value={selectedCollectionCenterId}
            onChange={(value: string) => setSelectedCollectionCenterId(value)}
            label="Collection Center"
            size="small"
            required={false}
            autoSelect={true}
            saveToStorage={true}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        sx={{ background: "#ffffff", borderRadius: "8px" }}
        justifyContent="space-between"
        alignItems="flex-start"
        paddingY={0}
        marginLeft={1}
        paddingBottom={1}
        marginRight={1}
        width={{ xs: "100%", md: "100%" }}
        padding={1}
        margin={"auto"}
        flexDirection={{ xs: "column", md: "row" }}
        gap={{ xs: 2, md: 0 }}
      >
        <Box>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: "none ",
              "& .MuiTab-root": {
                fontSize: "11px",
                minHeight: "30px",
                padding: "4px",
                fontWeight: 500,
                borderRadius: "5px",
                transition: "all 0.2s ease-in-out",
                border: "1px solid gray",
                color: "#495057",
                marginRight: "8px",
                marginTop: "20px",
              },
              "& .Mui-selected": {
                backgroundColor: "#1976d2",
                color: "#fff !important",
                fontWeight: 600,
              },
            }}
          >
            <Tab label="All" />
            <Tab label="Pending Dues" />
            <Tab label="Pending Sample Collection" />
          </Tabs>
        </Box>
        <Box display="flex" gap={1} alignItems="center" justifyContent="center">
          <Grid
            display={"flex"}
            alignItems="start"
            justifyContent="flex-start"
            flexDirection={{ xs: "column", sm: "column" }}
          >
            <Typography sx={{ fontSize: { md: "14px", xs: "12px" } }}>
              Search By:
            </Typography>
            <Select
              className="custom-input"
              value={search}
              placeholder="Search By"
              onChange={handleSearchTypeChange}
              sx={{
                borderRadius: "8px",
                background: "#F5F5FB",
                fontSize: "14px",
                height: "32px",
                "& fieldset": { border: "none" },
              }}
              input={<OutlinedInput />}
            >
              <MenuItem value="TRNo">TRNo</MenuItem>
              <MenuItem value="MRN">MRN</MenuItem>
              <MenuItem value="patientName">patientName</MenuItem>
              <MenuItem value="mobileNo">mobileNo</MenuItem>
              <MenuItem value="testName">Test Name</MenuItem>
              <MenuItem value="createdBy">Created By</MenuItem>
            </Select>
          </Grid>
          <Grid
            display={"flex"}
            alignItems="center"
            marginTop={2.4}
            flexDirection={{ xs: "column", sm: "column" }}
          >
            <TextField
              className="custom-input"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              placeholder={`Enter ${search}`}
              size="small"
              type={
                search === "patientName" || search === "mobileNo" || search === "testName" || search === "createdBy"
                  ? "text"
                  : "number"
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onKeyPress={handleKeyPress}
            />
          </Grid>
        </Box>
        <Box
          display="flex"
          gap={2}
          alignItems="end"
          flexDirection={{ xs: "column", sm: "row" }}
          width={{ xs: "100%", md: "auto" }}
          sx={{
            "& > *": {
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "120px" },
            },
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Date Limit
            </Typography>
            <Select
              size="small"
              value={dateRange}
              onChange={e => handleDateRangeChange(e.target.value)}
              className="custom-input"
              sx={{ minWidth: "120px", width: { xs: "100%", sm: "120px" } }}
            >
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="Yesterday">Yesterday</MenuItem>
              <MenuItem value="Last 7 days">Last 7 days</MenuItem>
              <MenuItem value="Last 1 month">Last 1 month</MenuItem>
              <MenuItem value="Last 6 months">Last 6 months</MenuItem>
              <MenuItem value="Last 1 year">Last 1 year</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </Box>

          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              From Date
            </Typography>
            <TextField
              className="custom-input"
              type="date"
              size="small"
              value={fromDate}
              onChange={e => {
                setFromDate(e.target.value);
                setDateRange("custom");
              }}
              sx={{ minWidth: "140px", width: { xs: "100%", sm: "140px" } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              To Date
            </Typography>
            <TextField
              className="custom-input"
              type="date"
              size="small"
              value={toDate}
              onChange={e => {
                setToDate(e.target.value);
                setDateRange("custom");
              }}
              sx={{ minWidth: "140px", width: { xs: "100%", sm: "140px" } }}
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "8px",
          height: "auto",
          padding: "16px 0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #e9ecef",
          display: "flex",
          flexDirection: "column",
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "auto",
            width:
              samples.length === 0 || loading
                ? "100%"
                : {
                  xs: "1600px",
                  sm: "1600px",
                  md: "1600px",
                  lg: "1600px",
                },
            minWidth: { xs: "100%", sm: "100%", md: "100%", lg: "100%" },
            "&::-webkit-scrollbar": {
              height: "12px",
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f3f4",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#a8a8a8",
              },
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: "#f1f3f4",
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 6,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#1976d2" }} />
              <Typography sx={{ color: "#6c757d", fontSize: "14px" }}>
                Loading patient data...
              </Typography>
            </Box>
          ) : samples.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography
                sx={{ color: "#6c757d", fontSize: "16px", fontWeight: "500" }}
              >
                No patient samples found
              </Typography>
            </Box>
          ) : (
            samples?.map((row, index) => (
              <Box
                key={index}
                sx={{
                  border: "1px solid #e9ecef",
                  mb: 0.5,
                  mx: 2,
                  borderRadius: "8px",
                  padding: "0",
                  backgroundColor: "#f8f9fa",
                  overflow: "hidden",
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    transform: "translateY(-1px)",
                    transition: "all 0.2s ease-in-out",
                  },
                }}
              >
                {/* Payment/Summary Row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(15, 1fr)",
                      sm: "repeat(15, 1fr)",
                      md: "repeat(15, 1fr)",
                    },
                    backgroundColor: "#1976d2",
                    alignItems: "center",
                    gap: 2,
                    px: 1,
                    paddingY: 0.5,
                    borderBottom: "1px solid #e9ecef",
                    borderRadius: "6px 6px 0 0",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#fff",
                        fontWeight: "600",
                      }}
                    >
                      Actions
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                    TR No
                  </Typography>

                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                    M/R No.
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                   REF MRN
                  </Typography>
                   <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                   Referred By
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                   P. Services
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                    patient Name
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                    Patient Phone#
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                    Age / Gender
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                    patient Type
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#fff" }}>
                    Gross Amount(Rs)
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#fff" }}>
                    Discount(Rs)
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#fff" }}>
                    Net Amount(Rs)
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#fff" }}>
                    paid Amount(Rs)
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}
                  >
                    Due Amount(Rs)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(15, 1fr)",
                      sm: "repeat(15, 1fr)",
                      md: "repeat(15, 1fr)",
                    },
                    alignItems: "center",
                    gap: 2,
                    p: 0,
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e9ecef",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    {/* <IconButton size="small" sx={{ color: "#1976d2" }}>
                      <Search sx={{ fontSize: "16px" }} />
                    </IconButton> */}
                    <Tooltip
                      placement="top"
                      arrow
                      title={
                        !labAccess?.can_edit ? "No access to edit" : "Edit"
                      }
                    >
                      <span>
                        <IconButton
                          onClick={() => handleEditClick(row)}
                          size="small"
                          disabled={!labAccess?.can_edit}
                          sx={{
                            color: labAccess?.can_edit ? "#1976d2" : "#ccc",
                            padding: 0,
                            cursor: labAccess?.can_edit
                              ? "pointer"
                              : "not-allowed",
                          }}
                        >
                          <Edit sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      placement="top"
                      arrow
                      title={
                        !labAccess?.can_delete
                          ? "No access to delete"
                          : "Delete"
                      }
                    >
                      <span>
                        <IconButton
                          onClick={() => handleDeleteClick(row?._id)}
                          size="small"
                          disabled={!labAccess?.can_delete}
                          sx={{
                            color: labAccess?.can_delete ? "red" : "#ccc",
                            padding: 0,
                            cursor: labAccess?.can_delete
                              ? "pointer"
                              : "not-allowed",
                          }}
                        >
                          <Delete sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      placement="top"
                      arrow
                      title={
                        !labAccess?.can_print_invoice
                          ? "No access to print invoice"
                          : "Print payment Details"
                      }
                    >
                      <span>
                        <ReceiptIcon
                          sx={{
                            color: labAccess?.can_print_invoice
                              ? "green"
                              : "#ccc",
                            cursor: labAccess?.can_print_invoice
                              ? "pointer"
                              : "not-allowed",
                            fontSize: 16,
                          }}
                          onClick={
                            labAccess?.can_print_invoice
                              ? () => getBillData({ id: row._id })
                              : undefined
                          }
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      placement="top"
                      arrow
                      title={
                        !labAccess?.can_print_invoice
                          ? "No access to print invoice"
                          : "Thermal Print Invoice"
                      }
                    >
                      <span>
                        <LocalPrintshopIcon
                          sx={{
                            color: labAccess?.can_print_invoice
                              ? "#7b1fa2"
                              : "#ccc",
                            cursor: labAccess?.can_print_invoice
                              ? "pointer"
                              : "not-allowed",
                            fontSize: 16,
                          }}
                          onClick={
                            labAccess?.can_print_invoice
                              ? () =>
                                  getBillData({
                                    id: row._id,
                                    format: "thermal",
                                  })
                              : undefined
                          }
                        />
                      </span>
                    </Tooltip>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#495057",
                      fontWeight: "500",
                    }}
                  >
                    {row?.trNo || `--`}
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>
                    {row?.patientData.mrn || "--"}
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>
                    {row?.patientData.ref_mrn || "--"}
                  </Typography>

                     <Typography sx={{ fontSize: "11px" }}>
                    {row?.otherDetails?.referredBy || ""}
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>
                    {row?.otherDetails?.requestSource || ""}
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>
                    {row?.patientData.patientName || "N/A"}
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>
                    {row?.patientData.phonNumber || "--"}
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>
                    {calculateAge(
                      row?.patientData.dob,
                      row?.testResults?.[0]?.createdAt || row?.createdAt
                    ) || "--"}{" "}
                    / {row?.patientData.gender || "--"}
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>
                    {row?.patientTypeData?.patientTypeName || "--"}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                    {row?.paymentDetail?.grossAmount}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                    {row?.paymentDetail?.discountInAmount || "0.00"}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                    {row?.paymentDetail?.netAmount || "0.00"}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                    {row?.paymentDetail?.nowPaying || "0.00"}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "red", fontWeight: "600" }}
                  >
                    {row?.paymentDetail?.dueAmount || "0.00"}
                  </Typography>
                </Box>

                {/* Header Row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(8, 1fr) ", // first 6 columns = 1fr, last 2 = 2fr
                      sm: "repeat(8, 1fr) ",
                      md: "repeat(8, 1fr) ",
                    },
                    backgroundColor: "lightgray",
                    borderBottom: "1px solid #e9ecef",
                  }}
                >
                  {/* <Box sx={{ minWidth: "130px", p: 1, borderRight: "1px solid #e9ecef" }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: "600", color: "#000" }}>
                        T/R No.
                      </Typography>
                  </Box> */}
                  <Box sx={{ p: 0.5, borderRight: "1px solid #e9ecef" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#000",
                      }}
                    >
                      Sr
                    </Typography>
                  </Box>
                  <Box sx={{ p: 0.5, borderRight: "1px solid #e9ecef" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#000",
                      }}
                    >
                      Test Code
                    </Typography>
                  </Box>
                  <Box sx={{ p: 0.5, borderRight: "1px solid #e9ecef" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#000",
                      }}
                    >
                      Sample Type
                    </Typography>
                  </Box>

                  <Box sx={{ p: 0.5, borderRight: "1px solid #e9ecef" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#000",
                      }}
                    >
                      Test name(s)
                    </Typography>
                  </Box>
                  <Box sx={{ p: 0.5, borderRight: "1px solid #e9ecef" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#000",
                      }}
                    >
                      Collection Date
                    </Typography>
                  </Box>
                  <Box sx={{ p: 0.5, borderRight: "1px solid #e9ecef" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#000",
                      }}
                    >
                      Reporting Date
                    </Typography>
                  </Box>
                  <Box sx={{ p: 0.5, borderRight: "1px solid #e9ecef" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#000",
                      }}
                    >
                      Created By
                    </Typography>
                  </Box>
                  <Box sx={{ p: 0.5, borderRight: "1px solid #e9ecef" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#000",
                      }}
                    >
                      Created on
                    </Typography>
                  </Box>
                </Box>

                {/* Data Row */}
                {/* Data Rows */}
                {row?.testResults?.map((testItem: any, idx: number) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "grid",
                      backgroundColor: "#ffffff",
                      gridTemplateColumns: {
                        xs: "repeat(8, 1fr) ",
                        sm: "repeat(8, 1fr) ",
                        md: "repeat(8, 1fr) ",
                      },
                      borderBottom: "1px solid #e9ecef",
                    }}
                  >
                    <Box sx={{ paddingLeft: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                        {idx + 1}
                      </Typography>
                    </Box>
                    <Box sx={{ paddingLeft: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                        {testItem?.testCode || "--"}
                      </Typography>
                    </Box>
                    <Box sx={{ paddingLeft: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#495057",
                          fontWeight: "500",
                        }}
                      >
                        {row?.testToPerform.find(
                          (item: any) => item.testId === testItem.testId
                        )?.sampleType || "--"}
                      </Typography>
                    </Box>

                    {/* Test name */}
                    <Box sx={{ paddingLeft: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                        {testItem?.testName || "N/A"}
                      </Typography>
                    </Box>

                    {/* Collection date */}
                    <Box sx={{ paddingLeft: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                        {(() => {
                          const sampleCollectionDateTime =
                            row?.testToPerform.find(
                              (item: any) => item.testId === testItem.testId
                            )?.sampleCollectionDateTime;

                          if (!sampleCollectionDateTime) {
                            return "--";
                          }

                          return new Date(
                            sampleCollectionDateTime
                          ).toLocaleString("en-GB", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          });
                        })()}
                      </Typography>
                    </Box>

                    {/* Reporting date */}
                    <Box sx={{ paddingLeft: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                        {(() => {
                          const reportingTime = row?.testToPerform.find(
                            (item: any) => item.testId === testItem.testId
                          )?.reportingTime;

                          if (!reportingTime) {
                            return "--";
                          }

                          return new Date(reportingTime).toLocaleString(
                            "en-GB",
                            {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          );
                        })()}
                      </Typography>
                    </Box>
                    <Box sx={{ paddingLeft: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                        {testItem.createdBy?.fullName ||
                          testItem?.createdBy?.firstName ||
                          "--"}
                      </Typography>
                    </Box>
                    <Box sx={{ paddingLeft: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: "#495057" }}>
                        {new Date(testItem?.createdAt).toLocaleString("en-GB", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        }) || "--"}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))
          )}
        </Box>
      </Box>
      <TablePagination
        component="div"
        count={totalAssets}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 25, 50, 100]}
        sx={{
          "& .MuiTablePagination-toolbar": {
            color: "#495057",
            fontSize: "14px",
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
          {
            fontWeight: "500",
          },
        }}
      />

      <Modal open={openDeleteModal} onClose={handleCancelDelete}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
            borderRadius: "16px",
            p: 4,
            width: "400px",
            border: "1px solid #e3f2fd",
          }}
        >
          <Typography
            sx={{
              color: "#212529",
              fontSize: "18px",
              fontWeight: "600",
              mb: 2,
              textAlign: "center",
            }}
          >
            Confirm Deletion
          </Typography>
          <Typography
            sx={{
              color: "#6c757d",
              fontSize: "14px",
              mb: 3,
              textAlign: "center",
            }}
          >
            Are you sure you want to delete this patient sample? This action
            cannot be undone.
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              onClick={handleCancelDelete}
              sx={{
                color: "#6c757d",
                borderColor: "#dee2e6",
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                  borderColor: "#adb5bd",
                },
                borderRadius: "8px",
                px: 3,
                py: 1,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              sx={{
                backgroundColor: "#dc3545",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "#c82333",
                },
                borderRadius: "8px",
                px: 3,
                py: 1,
              }}
              variant="contained"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
      {openPrintDialogPayment &&
        billData &&
        (printFormat === "thermal" ? (
          <ThermalBillReport
            open={openPrintDialogPayment}
            data={billData}
            onClose={() => setOpenPrintDialogPayment(false)}
          />
        ) : (
          <BillReport
            open={openPrintDialogPayment}
            data={billData}
            onClose={() => setOpenPrintDialogPayment(false)}
          />
        ))}
    </div>
  );
}

export default Samples;
